/**
 * "Show by:" — the minimal reporting-level control, client observation P1(c).
 *
 * ## What it is for
 *
 * The 2025 MELS baseline is estimated at two strata that have nothing to do with each other:
 * the four countries, and the ten watersheds with both a polygon and a survey. Both are
 * programme-wide, neither is above or below the other, and no §F5.8 cascade selection implies
 * either — the cascade drills *down* through the admin tree and the watershed rung filters to
 * **one** watershed. So the view the client asked for in as many words — *"for the watersheds
 * with both spatial data and information please show that as well on the map"* — was
 * unreachable from the interface, and had been since review R5 removed the level radios.
 *
 * ## Why it is two buttons and not five radios
 *
 * R5 was right about the radios. They offered every level for every indicator, standing next
 * to a cascade that had already decided, so four of the five were either wrong or redundant
 * and the reader's job was to work out which. This control is the opposite shape:
 *
 * * it renders **only levels that have data** for the mapped indicator, in this geography
 *   (`chooserLevels` → `effectiveLevels` ∩ `reachableLevels`);
 * * it renders **only when there is more than one** — one level is not a choice, and zero is
 *   the empty state the legend already explains;
 * * it renders **only for `aggregation: 'none'`** series. Every roll-up indicator can be
 *   valued at every level, so for those the cascade is still the whole answer and this
 *   component returns null. All 27 catalogue indicators are unaffected.
 *
 * In practice that is: two buttons, "Countries | Watersheds", for a baseline indicator on an
 * unfiltered map; one country filtered, still two (level 0 paints that country's own total —
 * §D3's `?parent=` is inclusive — and the watershed level its valued watersheds); a region or
 * commune filter, nothing at all, because a survey estimate has no rows down there and the
 * honest empty state is the answer.
 *
 * Labels are the §F5.8 boundary row names (`hierarchy.rows.*`), which already exist in both
 * languages and are the words the Boundaries panel uses for the same polygons — so "Watersheds"
 * means the same thing in both places.
 */
import { useTranslation } from 'react-i18next';

import { Seg } from '../../components/Seg';
import type { Indicator } from '../../lib/api';
import {
  chooserLevels,
  useAppStore,
  WATERSHED_LEVEL,
  PARTNERSHIP_LEVEL,
  type ReportingLevel,
} from '../../lib/state';

/**
 * Reporting level → the §F5.8 row label key. The same five names the Boundaries panel
 * lists, deliberately not `indicators.level*` ("Country level", "Region level"): those are
 * *descriptions* of a map, printed in the legend foot, and this control names the polygons a
 * click will draw.
 */
const ROW_LABEL_KEYS: Record<string, string> = {
  0: 'hierarchy.rows.adm0',
  1: 'hierarchy.rows.adm1',
  2: 'hierarchy.rows.adm2',
  [WATERSHED_LEVEL]: 'hierarchy.rows.wsheds',
  [PARTNERSHIP_LEVEL]: 'hierarchy.rows.partners',
};

export interface LevelChooserProps {
  /** The mapped indicator's catalogue row, or null while nothing is mapped. */
  meta: Indicator | null;
}

export function LevelChooser({ meta }: LevelChooserProps) {
  const { t } = useTranslation();
  const scope = useAppStore((s) => s.scope);
  const level = useAppStore((s) => s.reportingLevel);
  const setReportingLevel = useAppStore((s) => s.setReportingLevel);

  const levels = chooserLevels(meta, scope);
  if (levels.length < 2) return null;

  return (
    <div className="bw-lvl" data-levelchooser="">
      <span className="bw-lvl-label">{t('hierarchy.level.showBy')}</span>
      <Seg
        className="seg bw-lvl-seg"
        ariaLabel={t('hierarchy.level.showBy')}
        // `String(level)` because `Seg` is keyed on a string union while `reportingLevel` is
        // `0 | 1 | 2 | 'watershed' | 'partnership'`. `onChange` looks the picked value back
        // up in `levels` rather than parsing it, so the round trip cannot invent a level the
        // indicator does not have — the two representations meet in exactly one place.
        value={String(level)}
        options={levels.map((l) => ({
          value: String(l),
          label: t(ROW_LABEL_KEYS[String(l)] ?? 'hierarchy.rows.adm1'),
        }))}
        onChange={(value) => {
          const picked = levels.find((l) => String(l) === value);
          if (picked !== undefined) setReportingLevel(picked as ReportingLevel);
        }}
      />
    </div>
  );
}

export default LevelChooser;
